import { ChangeEvent, DragEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  artifactUrl,
  getDefaultRules,
  getHealth,
  getJob,
  getReport,
  getTextArtifact,
  listJobs,
  submitAnalyze,
  submitConvert,
  submitSolve
} from './api';
import type {
  Artifact,
  ConversionReport,
  FingeringPreviewRow,
  FretRules,
  HealthResponse,
  JobRecord,
  PipelineStage,
  RecognitionPreviewRow,
  ReportMessage
} from './types';

const REPOSITORY_URL = 'https://github.com/otcan/fretsolver';

const DEFAULT_RULES: FretRules = {
  tuning: ['E2', 'A2', 'D3', 'G3', 'B3', 'E4'],
  min_fret: 4,
  max_fret: 12,
  allow_open: false,
  preferred_fret: null,
  max_string_jump: null,
  prefer_high_strings: true,
  monophonic_only: false
};

function bytes(value?: number | null): string {
  if (value == null) return 'unknown size';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function compactJobId(jobId?: string): string {
  return jobId ? `${jobId.slice(0, 8)}…${jobId.slice(-4)}` : 'no job';
}

function isTerminal(status?: string): boolean {
  return status === 'succeeded' || status === 'failed' || status === 'awaiting_review';
}

function pickArtifact(artifacts: Artifact[], name: string): Artifact | undefined {
  return artifacts.find((artifact) => artifact.name === name);
}

export default function App() {
  const apiBase = import.meta.env.VITE_API_BASE || '';
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [rules, setRules] = useState<FretRules>(DEFAULT_RULES);
  const [job, setJob] = useState<JobRecord | null>(null);
  const [report, setReport] = useState<ConversionReport | null>(null);
  const [jobs, setJobs] = useState<JobRecord[]>([]);
  const [tabPreview, setTabPreview] = useState('No tab yet.');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);

  const refreshHealth = useCallback(async () => {
    try {
      setHealthError(null);
      const value = await getHealth(apiBase);
      setHealth(value);
    } catch (err) {
      setHealth(null);
      setHealthError(err instanceof Error ? err.message : String(err));
    }
  }, [apiBase]);

  const refreshJobs = useCallback(async () => {
    try {
      const value = await listJobs(apiBase);
      setJobs(value.jobs ?? []);
    } catch {
      // Recent jobs are useful but non-critical. Keep the current screen stable.
    }
  }, [apiBase]);

  const loadDefaultRules = useCallback(async () => {
    try {
      setRules(await getDefaultRules(apiBase));
    } catch {
      setRules(DEFAULT_RULES);
    }
  }, [apiBase]);

  const clearPolling = useCallback(() => {
    if (pollRef.current) {
      window.clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const pollJob = useCallback(
    async (jobId: string) => {
      clearPolling();
      const tick = async () => {
        try {
          const [nextJob, nextReport] = await Promise.all([getJob(apiBase, jobId), getReport(apiBase, jobId)]);
          setJob(nextJob);
          setReport(nextReport);
          await refreshJobs();
          if (!isTerminal(nextJob.status)) {
            pollRef.current = window.setTimeout(tick, 1200);
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : String(err));
          pollRef.current = window.setTimeout(tick, 2500);
        }
      };
      await tick();
    },
    [apiBase, clearPolling, refreshJobs]
  );

  useEffect(() => {
    void refreshHealth();
    void refreshJobs();
    void loadDefaultRules();
  }, [refreshHealth, refreshJobs, loadDefaultRules]);

  useEffect(() => () => clearPolling(), [clearPolling]);

  useEffect(() => {
    const artifact = pickArtifact(job?.artifacts ?? [], 'preview.tab');
    if (!artifact) {
      setTabPreview('No tab yet.');
      return;
    }
    void getTextArtifact(apiBase, artifact.url)
      .then(setTabPreview)
      .catch((err) => setTabPreview(`Could not load preview.tab: ${err instanceof Error ? err.message : String(err)}`));
  }, [apiBase, job?.artifacts]);

  async function startAnalyze() {
    if (!file) {
      setError('Choose a PDF, image, MusicXML, or MXL file first.');
      return;
    }
    setBusy(true);
    setError(null);
    setJob(null);
    setReport(null);
    setTabPreview('No tab yet.');
    try {
      const record = await submitAnalyze(apiBase, file);
      setJob(record);
      await pollJob(record.job_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function startConvert() {
    if (!file) {
      setError('Choose a PDF, image, MusicXML, or MXL file first.');
      return;
    }
    setBusy(true);
    setError(null);
    setJob(null);
    setReport(null);
    setTabPreview('No tab yet.');
    try {
      const record = await submitConvert(apiBase, file, sanitizeRules(rules));
      setJob(record);
      await pollJob(record.job_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function solveReviewed() {
    if (!job) return;
    setBusy(true);
    setError(null);
    try {
      const record = await submitSolve(apiBase, job.job_id, sanitizeRules(rules));
      setJob(record);
      await pollJob(record.job_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function selectExistingJob(nextJob: JobRecord) {
    clearPolling();
    setError(null);
    setJob(nextJob);
    setReport(null);
    try {
      const value = await getReport(apiBase, nextJob.job_id);
      setReport(value);
      if (!isTerminal(nextJob.status)) await pollJob(nextJob.job_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  const progress = job?.progress_percent ?? report?.progress_percent ?? 0;
  const artifacts = job?.artifacts ?? [];
  const canSolve = job?.status === 'awaiting_review';
  const imageArtifacts = artifacts.filter((artifact) => artifact.content_type?.startsWith('image/'));
  const tabSvgArtifact = pickArtifact(artifacts, 'tab.svg');

  return (
    <div className="app-shell">
      <header className="hero">
        <div>
          <p className="eyebrow">FretSolver v{health?.version ?? '0.3.2'}</p>
          <h1>Convert score images into rule-bound guitar TAB.</h1>
          <p className="subhead">
            Upload PDF, screenshot, printed TAB image, MusicXML, or MXL. Review the recognized music, tune the fret rules, then generate real MusicXML notation with TAB.
          </p>
          <div className="hero-links">
            <a href={REPOSITORY_URL} target="_blank" rel="noreferrer">Open source on GitHub</a>
            <span>MIT licensed</span>
          </div>
        </div>
        <HealthCard health={health} error={healthError} onRefresh={refreshHealth} />
      </header>

      <main className="workspace">
        <aside className="left-rail">
          <UploadPanel file={file} onFile={setFile} busy={busy} onAnalyze={startAnalyze} onConvert={startConvert} />
          <RuleEditor rules={rules} onChange={setRules} canSolve={canSolve} busy={busy} onSolve={solveReviewed} />
          <RecentJobs jobs={jobs} activeJobId={job?.job_id} onSelect={(item) => void selectExistingJob(item)} />
        </aside>

        <section className="main-column">
          <section className="panel status-panel">
            <PanelHeader title="Job" badge={compactJobId(job?.job_id)} />
            <div className="status-strip">
              <StatusBadge value={job?.status ?? 'idle'} />
              <span>{job?.current_stage ? `stage: ${job.current_stage}` : 'No active stage'}</span>
              {job?.input_filename ? <span className="truncate">input: {job.input_filename}</span> : null}
            </div>
            <div className="progress-track"><div className="progress-fill" style={{ width: `${progress}%` }} /></div>
            {error ? <div className="callout error"><strong>Something broke</strong><span>{error}</span></div> : null}
            <Pipeline stages={report?.stages ?? []} />
            <Messages messages={report?.messages ?? job?.messages ?? []} />
          </section>

          <section className="panel">
            <PanelHeader title="Recognition report" badge={report?.quality?.label ?? 'waiting'} />
            <SummaryCards report={report} />
            <div className="two-col">
              <RecognitionTable rows={report?.recognition_preview ?? []} />
              <DebugImages apiBase={apiBase} artifacts={imageArtifacts} />
            </div>
          </section>

          <section className="panel">
            <PanelHeader title="Generated fingering" badge="review the solution" />
            <div className="two-col">
              <div>
                <Fretboard rules={rules} rows={report?.fingering_preview ?? []} />
                <FingeringTable rows={report?.fingering_preview ?? []} />
              </div>
              <div>
                <TabNotation apiBase={apiBase} artifact={tabSvgArtifact} />
                <details className="debug-preview">
                  <summary>ASCII debug preview</summary>
                  <pre>{tabPreview}</pre>
                </details>
              </div>
            </div>
          </section>

          <Artifacts apiBase={apiBase} artifacts={artifacts} />
        </section>
      </main>
    </div>
  );
}

function sanitizeRules(rules: FretRules): FretRules {
  return {
    ...rules,
    tuning: rules.tuning.map((item) => item.trim()).filter(Boolean),
    preferred_fret: numberOrNull(rules.preferred_fret),
    max_string_jump: numberOrNull(rules.max_string_jump)
  };
}

function numberOrNull(value: number | null | undefined): number | null {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  return value;
}

function PanelHeader({ title, badge }: { title: string; badge: string }) {
  return (
    <div className="panel-header">
      <h2>{title}</h2>
      <span className="pill">{badge}</span>
    </div>
  );
}

function HealthCard({ health, error, onRefresh }: { health: HealthResponse | null; error: string | null; onRefresh: () => void }) {
  return (
    <div className="health-card">
      <div className="health-top">
        <strong>{health ? 'Backend online' : 'Backend offline'}</strong>
        <button className="tiny" onClick={onRefresh}>refresh</button>
      </div>
      {health ? (
        <>
          <span>Version {health.version}</span>
          <span>OMR {health.omr.available ? 'available' : 'not found'}</span>
          <span>TAB OCR {health.tab_ocr?.available ? 'available' : 'not found'}</span>
          <small>{health.omr.binary}</small>
          <span>Frontend {health.frontend?.served ? 'served' : 'not served'}</span>
        </>
      ) : (
        <small>{error ?? 'Not checked yet.'}</small>
      )}
    </div>
  );
}

function UploadPanel({ file, onFile, busy, onAnalyze, onConvert }: { file: File | null; onFile: (file: File | null) => void; busy: boolean; onAnalyze: () => void; onConvert: () => void }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  function handleDrop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setDragging(false);
    onFile(event.dataTransfer.files?.[0] ?? null);
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    onFile(event.target.files?.[0] ?? null);
  }

  return (
    <section className="panel">
      <PanelHeader title="Input" badge="PDF / TAB / XML" />
      <label
        className={`dropzone ${dragging ? 'dragging' : ''}`}
        onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => { event.preventDefault(); setDragging(false); }}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.webp,.tif,.tiff,.bmp,.musicxml,.xml,.mxl"
          onChange={handleChange}
        />
        <span className="drop-title">Drop a score here</span>
        <span className="drop-subtitle">{file ? file.name : 'or choose a score or TAB image'}</span>
        {file ? <span className="drop-size">{bytes(file.size)}</span> : null}
      </label>
      <div className="button-grid">
        <button disabled={busy} onClick={onAnalyze}>Analyze only</button>
        <button className="secondary" disabled={busy} onClick={onConvert}>Analyze + solve</button>
      </div>
      <p className="hint">Use analyze-only for scanned/PDF input. It pauses before fingering so bad OMR does not silently become bad TAB.</p>
    </section>
  );
}

function RuleEditor({ rules, onChange, canSolve, busy, onSolve }: { rules: FretRules; onChange: (rules: FretRules) => void; canSolve: boolean; busy: boolean; onSolve: () => void }) {
  function update<K extends keyof FretRules>(key: K, value: FretRules[K]) {
    onChange({ ...rules, [key]: value });
  }

  function numberValue(value: string): number | null {
    return value.trim() === '' ? null : Number(value);
  }

  return (
    <section className="panel">
      <PanelHeader title="Rules" badge={`${rules.min_fret}-${rules.max_fret} frets`} />
      <div className="rule-grid">
        <label><span>Min fret</span><input type="number" min={0} max={24} value={rules.min_fret} onChange={(event) => update('min_fret', Number(event.target.value))} /></label>
        <label><span>Max fret</span><input type="number" min={0} max={24} value={rules.max_fret} onChange={(event) => update('max_fret', Number(event.target.value))} /></label>
        <label><span>Preferred fret</span><input type="number" min={0} max={24} value={rules.preferred_fret ?? ''} onChange={(event) => update('preferred_fret', numberValue(event.target.value))} /></label>
        <label><span>Max string jump</span><input type="number" min={0} max={6} value={rules.max_string_jump ?? ''} onChange={(event) => update('max_string_jump', numberValue(event.target.value))} /></label>
      </div>
      <label className="field-label" htmlFor="tuning">Tuning, low to high</label>
      <input id="tuning" value={rules.tuning.join(' ')} onChange={(event) => update('tuning', event.target.value.split(/[\s,]+/).filter(Boolean))} />
      <div className="checks">
        <label><input type="checkbox" checked={rules.allow_open} onChange={(event) => update('allow_open', event.target.checked)} /> Allow open strings</label>
        <label><input type="checkbox" checked={rules.prefer_high_strings} onChange={(event) => update('prefer_high_strings', event.target.checked)} /> Prefer high strings</label>
      </div>
      <button className="primary-wide" disabled={!canSolve || busy} onClick={onSolve}>Generate TAB from reviewed notes</button>
    </section>
  );
}

function RecentJobs({ jobs, activeJobId, onSelect }: { jobs: JobRecord[]; activeJobId?: string; onSelect: (job: JobRecord) => void }) {
  return (
    <section className="panel recent-panel">
      <PanelHeader title="Recent jobs" badge={`${jobs.length}`} />
      {jobs.length === 0 ? <p className="hint">No jobs yet.</p> : null}
      <div className="job-list">
        {jobs.slice(0, 8).map((item) => (
          <button key={item.job_id} className={`job-row ${item.job_id === activeJobId ? 'active' : ''}`} onClick={() => onSelect(item)}>
            <span>{compactJobId(item.job_id)}</span>
            <small>{item.status} · {item.input_filename ?? 'upload'}</small>
          </button>
        ))}
      </div>
    </section>
  );
}

function StatusBadge({ value }: { value: string }) {
  return <span className={`status-badge ${value}`}>{value}</span>;
}

function Pipeline({ stages }: { stages: PipelineStage[] }) {
  if (!stages.length) return <p className="hint">Start a job to see the pipeline timeline.</p>;
  return (
    <div className="timeline">
      {stages.map((stage) => (
        <div key={stage.name} className={`stage ${stage.status}`}>
          <strong>{stage.label}</strong>
          <small>{stage.status}{stage.duration_ms != null ? ` · ${stage.duration_ms} ms` : ''}</small>
          {stage.message ? <span>{stage.message}</span> : null}
        </div>
      ))}
    </div>
  );
}

function Messages({ messages }: { messages: ReportMessage[] }) {
  if (!messages.length) return null;
  return (
    <div className="messages">
      {messages.slice(-10).map((message, index) => (
        <div key={`${message.code}-${index}`} className={`message ${message.severity}`}>
          <strong>{message.message}</strong>
          <code>{message.code}{message.stage ? ` · ${message.stage}` : ''}</code>
        </div>
      ))}
    </div>
  );
}

function SummaryCards({ report }: { report: ConversionReport | null }) {
  const score = report?.score_summary ?? {};
  const quality = report?.quality ?? {};
  const solver = report?.solver_summary ?? {};
  const items = [
    ['Title', score.title ?? 'unknown'],
    ['Measures', score.measure_count ?? '-'],
    ['Notes', score.note_count ?? '-'],
    ['Rests', score.rest_count ?? '-'],
    ['Pitch range', score.pitch_range?.low ? `${score.pitch_range.low} to ${score.pitch_range.high}` : '-'],
    ['Quality', quality.score != null ? `${quality.score}/100` : '-'],
    ['Tabbed notes', solver.tabbed_note_count ?? '-'],
    ['Chord groups', solver.chord_group_count ?? '-'],
    ['Total cost', solver.total_cost ?? '-']
  ];
  return (
    <div className="cards">
      {items.map(([label, value]) => (
        <div className="card" key={label}>
          <div className="value">{value}</div>
          <div className="label">{label}</div>
        </div>
      ))}
    </div>
  );
}

function RecognitionTable({ rows }: { rows: RecognitionPreviewRow[] }) {
  return (
    <div>
      <h3>Recognized notes</h3>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Measure</th><th>Beat</th><th>Kind</th><th>Note</th><th>Duration</th><th>Voice</th><th>Chord</th></tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={7}>No recognized events yet.</td></tr> : null}
            {rows.map((row, index) => (
              <tr key={row.event_id ?? index}>
                <td>{row.measure ?? '-'}</td>
                <td>{row.beat ?? '-'}</td>
                <td>{row.kind ?? '-'}</td>
                <td>{row.note ?? '-'}</td>
                <td>{row.duration_quarters ?? '-'}</td>
                <td>{row.voice ?? '-'}</td>
                <td>{row.chord ? 'yes' : 'no'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FingeringTable({ rows }: { rows: FingeringPreviewRow[] }) {
  return (
    <div>
      <h3>Fingering preview</h3>
      <div className="table-wrap compact-table">
        <table>
          <thead><tr><th>Measure</th><th>Beat</th><th>Note</th><th>String</th><th>Fret</th><th>Cost</th></tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={6}>No solved fingering yet.</td></tr> : null}
            {rows.map((row, index) => (
              <tr key={row.event_id ?? index}>
                <td>{row.measure ?? '-'}</td>
                <td>{row.beat ?? '-'}</td>
                <td>{row.note ?? '-'}</td>
                <td>{row.string ?? '-'}</td>
                <td>{row.fret ?? '-'}</td>
                <td>{row.cost_to_here ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DebugImages({ apiBase, artifacts }: { apiBase: string; artifacts: Artifact[] }) {
  if (!artifacts.length) return <div><h3>Debug images</h3><p className="hint">No image artifacts yet.</p></div>;
  return (
    <div>
      <h3>Debug images</h3>
      <div className="image-grid">
        {artifacts.map((artifact) => (
          <a key={artifact.name} href={artifactUrl(apiBase, artifact.url)} target="_blank" rel="noreferrer" className="image-card">
            <span>{artifact.name}</span>
            <img src={artifactUrl(apiBase, artifact.url)} alt={artifact.name} />
          </a>
        ))}
      </div>
    </div>
  );
}

function TabNotation({ apiBase, artifact }: { apiBase: string; artifact?: Artifact }) {
  if (!artifact) {
    return (
      <div className="tab-notation">
        <h3>TAB notation</h3>
        <p className="hint">Generate TAB to see notation output.</p>
      </div>
    );
  }

  const url = artifactUrl(apiBase, artifact.url);
  return (
    <div className="tab-notation">
      <div className="notation-heading">
        <h3>TAB notation</h3>
        <a href={url} target="_blank" rel="noreferrer">Open SVG</a>
      </div>
      <a href={url} target="_blank" rel="noreferrer" className="tab-svg-frame">
        <img src={url} alt="Generated TAB notation" />
      </a>
    </div>
  );
}

function Fretboard({ rules, rows }: { rules: FretRules; rows: FingeringPreviewRow[] }) {
  const frets = useMemo(() => {
    const start = Math.max(0, rules.min_fret);
    const end = Math.min(24, rules.max_fret);
    return Array.from({ length: Math.max(0, end - start + 1) }, (_, index) => start + index);
  }, [rules.min_fret, rules.max_fret]);
  const strings = [...rules.tuning].reverse();
  const dots = new Map<string, FingeringPreviewRow[]>();
  rows.slice(0, 60).forEach((row) => {
    if (row.string == null || row.fret == null) return;
    const key = `${row.string}:${row.fret}`;
    dots.set(key, [...(dots.get(key) ?? []), row]);
  });

  return (
    <div className="fretboard-wrap">
      <h3>Fretbox map</h3>
      <div className="fretboard" style={{ gridTemplateColumns: `72px repeat(${frets.length}, minmax(38px, 1fr))` }}>
        <div className="fret-head">String</div>
        {frets.map((fret) => <div key={fret} className="fret-head">{fret}</div>)}
        {strings.map((label, index) => {
          const stringNumber = index + 1;
          return (
            <div className="fret-row" key={stringNumber} style={{ display: 'contents' }}>
              <div className="string-label">{stringNumber} · {label}</div>
              {frets.map((fret) => {
                const cell = dots.get(`${stringNumber}:${fret}`) ?? [];
                return (
                  <div key={`${stringNumber}-${fret}`} className="fret-cell">
                    {cell.slice(0, 2).map((row, i) => <span key={`${row.event_id ?? row.note}-${i}`} className="note-dot">{row.note}</span>)}
                    {cell.length > 2 ? <span className="more-dot">+{cell.length - 2}</span> : null}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Artifacts({ apiBase, artifacts }: { apiBase: string; artifacts: Artifact[] }) {
  return (
    <section className="panel">
      <PanelHeader title="Artifacts" badge={`${artifacts.length} files`} />
      {artifacts.length === 0 ? <p className="hint">No artifacts yet.</p> : null}
      <div className="artifact-list">
        {artifacts.map((artifact) => (
          <a key={artifact.name} href={artifactUrl(apiBase, artifact.url)} target="_blank" rel="noreferrer" className="artifact">
            <strong>{artifact.name}</strong>
            <small>{artifact.content_type} · {bytes(artifact.size_bytes)}</small>
          </a>
        ))}
      </div>
    </section>
  );
}
